/**
 * `can(roles, action)` for quotes and contracts (docs/04-security-and-tenancy.md §2; TJR inventory 01 §10.1; MB
 * decisions 121, 122, 127, 166). Roles are additive: a person may do an action when any of their roles allows it. RLS
 * in `supabase/migrations/20261018002000_quotes_contracts.sql` and `20261019200000_quotes_answers.sql` enforces the
 * same table; the apps use this only to show or hide controls.
 */
export type StaffRole = 'tenant_admin' | 'manager' | 'field_crew' | 'office_staff' | 'data_processor';

export type QuoteAction =
  /** Create quotes and edit the working (unsent) revision of a quote (decision 166: office staff too). */
  | 'quote.draft'
  /** Send a revision, record the client's answer (accept with the chosen option, decline). */
  | 'quote.send'
  /** Make contracts, issue them, record signing and variations. */
  | 'contract.write'
  /** See prices, rates and payment terms (decision 121: managers, tenant admins, office staff). */
  | 'commercial.read'
  /** Open the operator (OP) contract: every staff member. */
  | 'contract.op.read'
  /** Change QC limits (decision 122: manager or tenant admin; after signing only with a reason). */
  | 'qcspec.write'
  /** Edit the tenant's clause templates and legal identity. */
  | 'clauses.write'
  /** Edit the protected payment details (decision 127: tenant admin only). */
  | 'payment.write'
  /** Read the payment details (decision 127: tenant admin, manager, office staff). */
  | 'payment.read'
  /** Add and edit client organisations and contacts. */
  | 'clients.write';

const RULES: Readonly<Record<QuoteAction, readonly StaffRole[]>> = {
  'quote.draft': ['tenant_admin', 'manager', 'office_staff'],
  'quote.send': ['tenant_admin', 'manager'],
  'contract.write': ['tenant_admin', 'manager'],
  'commercial.read': ['tenant_admin', 'manager', 'office_staff'],
  'contract.op.read': ['tenant_admin', 'manager', 'office_staff', 'field_crew', 'data_processor'],
  'qcspec.write': ['tenant_admin', 'manager'],
  'clauses.write': ['tenant_admin'],
  'payment.write': ['tenant_admin'],
  'payment.read': ['tenant_admin', 'manager', 'office_staff'],
  'clients.write': ['tenant_admin', 'manager'],
};

export function can(roles: readonly string[], action: QuoteAction): boolean {
  const allowed = RULES[action];
  return roles.some((r) => (allowed as readonly string[]).includes(r));
}

/** Roles allowed an action, for help text and tests. */
export function rolesFor(action: QuoteAction): readonly StaffRole[] {
  return RULES[action];
}

/**
 * Quote statuses in which office staff may edit the working revision (decision 166: "office staff may draft quotes;
 * a manager sends them"). The working revision is never the one sent: "Send" freezes revision n in
 * `quote_revision` and edits continue as revision n + 1, so a sent or declined quote is drafted again for the next
 * revision. Once accepted, the quote belongs to the contract and only managers and tenant admins change it. RLS
 * (`public.quote_draftable_status`) holds the same list.
 */
export const OFFICE_DRAFT_STATUSES = ['draft', 'sent', 'declined'] as const;

/** May these roles edit this quote's working revision? */
export function canEditQuote(roles: readonly string[], status: string): boolean {
  if (can(roles, 'quote.send')) return true;
  return can(roles, 'quote.draft') && (OFFICE_DRAFT_STATUSES as readonly string[]).includes(status);
}
